import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import useLiveData from 'hooks/useLiveData';

export const useAccordionListQuery = ({ id }) => {
	const staticData = useStaticQuery(graphql`
	{
		allSmartTagsAccordionList(
			filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}
		) {
			edges {
				node {
					id
					flush
					groupByCategory
					options
					Audience {
						...AudienceRelationshipDetailsFragment
					}
					Category {
						object {
							id
							name
						}
					}
				}
			}
		}
		allAccordionsCategory(
			filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}
		) {
			edges {
				node {
					id
					name
					Accordion {
						object {
							title
							content
							sort
						}
					}
				}
			}
		}
	}
	`);
	const accordionListEdges = staticData?.allSmartTagsAccordionList?.edges || [];
	const accordionListNodes = accordionListEdges.map(({ node }) => node);
	const accordionCategoryEdges = staticData?.allAccordionsCategory?.edges || [];
	const accordionCategoryNodes = accordionCategoryEdges.map(({ node }) => node);

	const {
		statusText: accordionListStatusText,
		response: accordionListResponse,
		visibilityChange: accordionListVisibilityChange,
	} = useLiveData({
		namespace: 'SmartTags',
		model: 'AccordionList',
		method: 'all',
		graphqlKey: 'allSmartTagsAccordionList',
		apiParams: {
			approved: true,
			archived: false,
			deleted: false,
			_join: 'Audience,Category',
			...(!!id ? { id } : {}),
		}
	});

	const liveAccordionList = accordionListResponse?.data.find(item => item?.id === id);
	let accordionList = accordionListNodes.find(item => item?.id === id);
	if (!!liveAccordionList && accordionListStatusText === 'LOADED') {
		accordionList = liveAccordionList;
	}
	if (!!liveAccordionList && accordionListVisibilityChange) accordionList = liveAccordionList;

	const accordionListCategoryIds = accordionList?.Category?.map(({ object }) => object?.id);

	const {
		statusText: accordionCategoryStatusText,
		response: accordionCategoryResponse,
		visibilityChange: accordionCategoryVisibilityChange,
	} = useLiveData({
		namespace: 'Accordions',
		model: 'Category',
		method: 'all',
		graphqlKey: 'allAccordionsCategory',
		apiParams: {
			approved: true,
			archived: false,
			deleted: false,
			_join: 'Accordion',
			...(accordionListCategoryIds?.length ? { id: accordionListCategoryIds.join(',') } : {}),
		}
	});

	const liveAccordionCategory = accordionCategoryResponse?.data.filter(item => accordionListCategoryIds?.includes(item?.id));
	let accordionCategory = accordionCategoryNodes.filter(item => accordionListCategoryIds?.includes(item?.id));
	if (!!liveAccordionCategory && accordionCategoryStatusText === 'LOADED') {
		accordionCategory = liveAccordionCategory;
	}
	if (!!liveAccordionCategory && accordionCategoryVisibilityChange) accordionCategory = liveAccordionCategory;

	const options = accordionList?.options || [];
	const expandFirst = options?.includes('expandFirst');
	const expandAll = options?.includes('expandAll');

	let accordions = accordionCategory?.map((cat, catIndex) => { 
		const accordions = cat?.Accordion?.map(({ object }) => ({
				title: object?.title,
				content: object?.content,
				sort: object?.sort
			}))?.sort((a, b) => a?.sort - b?.sort)
		accordions.forEach((accordion, accordionIndex) => {
			accordion.expanded = accordion.expanded || false;
			if (expandFirst && accordionIndex === 0 && catIndex === 0) {
				accordion.expanded = true;
			}
			if (expandAll) {
				accordion.expanded = true;
			}
		});
		return ({
			name: cat?.name,
			accordions
		})
	});

	const data = React.useMemo(() => ({
		flush: accordionList?.flush,
		hideTitle: !accordionList?.groupByCategory,
		accordions,
	}), [accordionList, accordions]);

	return { data };
};